/**
 * The org-layer override form for one capability.
 *
 * A row per init field, excluding the framework-supplied agent_id /
 * capability_id and every field the global layer keeps locked. Each row is a
 * literal input or a "$secret reference" picker over the org's defined fields.
 * Saves via PUT.
 *
 * Which capability you are editing is answered by the card index that opens
 * this, not by a dropdown inside it — a picker at the top of a form makes the
 * form look like it belongs to the picker rather than to the thing picked.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { getCapabilityGlobalConfig } from '@optum/api-client/endpoints/capability-global-configs';
import { getCapabilitySchema } from '@optum/api-client/endpoints/capabilities';
import { listOrgSecrets } from '@optum/api-client/endpoints/organization-secrets';
import {
  deleteOrgCapabilityConfig,
  getOrgCapabilityConfig,
  setOrgCapabilityConfig,
} from '@optum/api-client/endpoints/org-capability-configs';
import type { FieldSpec } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import {
  ConfigFieldRow,
  clearStaleDependents,
  collectViolations,
  filterFrameworkFields,
  sanitizeAgainstSchema,
  type InheritedValue,
} from '../plugins/shared/init-config-form.js';

/** The baseline a field inherits when this org hasn't overridden it:
 *  the global default (layer 2) if present, else the capability's own
 *  schema default (layer 1). */
function inheritedFor(
  spec: FieldSpec,
  globalConfig: Record<string, unknown>,
): InheritedValue | undefined {
  if (Object.prototype.hasOwnProperty.call(globalConfig, spec.name)) {
    return { value: globalConfig[spec.name], label: 'global default' };
  }
  if (spec.has_default) return { value: spec.default, label: 'capability default' };
  return undefined;
}

export function CapabilityConfigForm({
  organizationId,
  capabilityId,
}: {
  organizationId: string;
  capabilityId: string;
}) {
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
  });
  const stored = useQuery({
    queryKey: qk.orgCapConfig(organizationId, capabilityId),
    queryFn: () => getOrgCapabilityConfig(organizationId, capabilityId),
  });
  const orgSecrets = useQuery({
    queryKey: qk.orgSecrets(organizationId),
    queryFn: () => listOrgSecrets(organizationId),
  });
  // Layer 2 of the merge — shown as the inherited baseline for fields this
  // org hasn't overridden. 404 = no global default set.
  const global = useQuery({
    queryKey: qk.capabilityGlobalConfig(capabilityId),
    queryFn: () => getCapabilityGlobalConfig(capabilityId),
    retry: (n, e) => (e instanceof ApiError && e.status === 404 ? false : n < 2),
  });
  const globalConfig = global.data?.config ?? {};

  const [values, setValues] = useState<Record<string, unknown>>({});
  const toastApi = useToast();
  useEffect(() => {
    if (!stored.data || !schema.data) return;
    // Auto-trim string values that exceed max_length on the schema. HTML
    // maxLength only constrains user typing — a stale over-length value
    // hydrated from the DB renders verbatim and would re-save unchanged.
    const { cleaned, trimmed } = sanitizeAgainstSchema(stored.data.config, schema.data.init_fields);
    // Stored overrides for fields no longer org-enabled would 422 on save
    // (and are ignored at task time) — drop them from the editable set.
    const scopes = global.data?.field_scopes ?? {};
    const editable = Object.fromEntries(
      Object.entries(cleaned).filter(([name]) => scopes[name]?.organization === true),
    );
    setValues(editable);
    if (trimmed.length > 0) {
      toastApi.info(
        'Trimmed stored values to schema',
        `${trimmed.join(', ')} — review and Save to persist.`,
      );
    }
  }, [stored.data, schema.data, global.data, toastApi]);

  // Re-validate whenever values change. If any constraint is violated, the
  // Save button stays disabled and the offending field shows an inline error.
  const violations = useMemo(
    () => collectViolations(values, schema.data?.init_fields ?? []),
    [values, schema.data],
  );

  const qc = useQueryClient();
  const toast = toastApi;
  const save = useMutation({
    mutationFn: () => setOrgCapabilityConfig(organizationId, capabilityId, values),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: qk.orgCapConfig(organizationId, capabilityId) });
      qc.invalidateQueries({ queryKey: qk.orgCapConfigs(organizationId) });
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });
  const reset = useMutation({
    mutationFn: () => deleteOrgCapabilityConfig(organizationId, capabilityId),
    onSuccess: () => {
      toast.success('Config cleared — capability will use its built-in defaults');
      setValues({});
      qc.invalidateQueries({ queryKey: qk.orgCapConfig(organizationId, capabilityId) });
      qc.invalidateQueries({ queryKey: qk.orgCapConfigs(organizationId) });
    },
    onError: (e) => {
      // 404 = nothing to delete, which is fine — treat as already-clean.
      if (e instanceof ApiError && e.status === 404) {
        setValues({});
        toast.success('Already clean');
        return;
      }
      toast.error('Reset failed', e instanceof ApiError ? e.message : String(e));
    },
  });

  // Keep only fields the operator may configure at this layer: the framework
  // supplies agent_id + capability_id at task-execution time, and fields
  // without the organization scope are locked to global — hidden here.
  const initFields: FieldSpec[] = useMemo(
    () =>
      filterFrameworkFields(schema.data?.init_fields ?? []).filter(
        (f) => (global.data?.field_scopes ?? {})[f.name]?.organization === true,
      ),
    [schema.data, global.data],
  );

  if (schema.isPending || stored.isPending || global.isPending) return <LoadingBlock />;
  if (schema.isError) {
    return (
      <ErrorBlock
        message={schema.error instanceof Error ? schema.error.message : 'Failed to load schema'}
      />
    );
  }
  if (initFields.length === 0) {
    return (
      <EmptyBlock>
        No fields are enabled for organization-level override. Enable them on the capability&apos;s
        global config.
      </EmptyBlock>
    );
  }

  return (
    <div style={{ borderRadius: 'var(--radius-chip)', border: '1px solid var(--border-hairline)' }}>
      <div
        style={{
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          overflowY: 'auto',
          padding: 11,
        }}
      >
        {initFields.map((spec) => (
          <ConfigFieldRow
            key={spec.name}
            spec={spec}
            value={values[spec.name]}
            siblings={values}
            {...(() => {
              const inh = inheritedFor(spec, globalConfig);
              return inh ? { inherited: inh } : {};
            })()}
            {...(violations[spec.name] !== undefined ? { violation: violations[spec.name]! } : {})}
            secretFieldKeys={(orgSecrets.data ?? []).map((e) => e.field_key)}
            onChange={(v) => {
              let next = { ...values };
              if (v === undefined) delete next[spec.name];
              else next[spec.name] = v;
              next = clearStaleDependents(initFields, next, spec.name);
              setValues(next);
            }}
          />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 7,
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          padding: 11,
        }}
      >
        <Button
          variant="ghost"
          onClick={() => reset.mutate()}
          disabled={reset.isPending || save.isPending}
        >
          {reset.isPending ? 'Resetting…' : 'Reset to defaults'}
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || Object.keys(violations).length > 0}
        >
          {save.isPending ? 'Saving…' : 'Save config'}
        </Button>
      </div>
    </div>
  );
}
